// Client-side downscale for photo uploads (phone camera photos are often 8-11MB
// at full resolution, which is slow to upload over cellular). Resizing before the
// FormData POST is what actually cuts upload time, since a server-side resize still
// requires the original file to reach the server first.
export const LOW_RES_MAX_DIMENSION = 1920;
export const LOW_RES_QUALITY = 0.8;

export async function resizeImageFile(file, { maxDimension = LOW_RES_MAX_DIMENSION, quality = LOW_RES_QUALITY } = {}) {
  if (!file?.type?.startsWith("image/") || file.type === "image/svg+xml") return file;

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = objectUrl;
    });

    const { naturalWidth: w, naturalHeight: h } = img;
    const scale = Math.min(1, maxDimension / Math.max(w, h));
    if (scale >= 1) return file;

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) return file;

    const newName = file.name.replace(/\.[^/.]+$/, "") + ".jpg";
    return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function resizeImageFiles(files, opts) {
  return Promise.all(files.map((f) => resizeImageFile(f, opts)));
}
